import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';
import { useRefetchCompletionStatus } from '@/hooks/useCompletionStatus';
import { ICompanyData, ICompanyDataListResponse } from '@/types/company-data.types';

export const useGetCompanyData = (tenantId: string | number | undefined) => {
  return useQuery({
    queryKey: ['company-data', tenantId],
    queryFn: async () => {
      if (!tenantId) throw new Error('Tenant ID is required');

      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/company-data`;
      const { data: response } = await axiosInstance.get<ICompanyDataListResponse>(url);

      const rawData = response.data?.data?.[0];
      if (!rawData) return null;

      // pic_names already comes as string[] from backend, no transformation needed
      return rawData;
    },
    enabled: !!tenantId,
    retry: (failureCount, error: unknown) => {
      const err = error as { response?: { status?: number } };
      if (err?.response?.status === 404) return false;
      return failureCount < 2;
    },
    onError: (error: unknown) => {
      const err = error as { response?: { status?: number } };
      if (err?.response?.status !== 404) {
        errorHandler(error);
      }
    },
  });
};

export const useCreateCompanyData = () => {
  const queryClient = useQueryClient();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  return useMutation({
    mutationFn: async ({ tenantId, payload }: { tenantId: string | number; payload: Partial<ICompanyData> }) => {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/company-data`;

      // pic_names already in correct format (array of strings)
      // Explicitly remove auto-populated fields to prevent overwriting backend logic
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { start_date, end_date, pic_names, ...cleanPayload } = payload as any;
      const { data } = await axiosInstance.post<ICompanyData>(url, cleanPayload);
      return data;
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries(['company-data', variables.tenantId]);
      await refetchCompletionStatus();
      toast.success('Data perusahaan berhasil dibuat');
    },
    onError: error => {
      errorHandler(error);
    },
  });
};

export const useUpdateCompanyData = () => {
  const queryClient = useQueryClient();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  return useMutation({
    mutationFn: async ({ tenantId, payload }: { tenantId: string | number; payload: Partial<ICompanyData> }) => {
      if (!payload.id) throw new Error('Company data ID is required for update');
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/company-data/${payload.id}`;

      // pic_names already in correct format (array of strings)
      // Explicitly remove auto-populated fields to prevent overwriting backend logic
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { start_date, end_date, pic_names, ...cleanPayload } = payload as any;
      const { data } = await axiosInstance.put<ICompanyData>(url, cleanPayload);
      return data;
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries(['company-data', variables.tenantId]);
      await refetchCompletionStatus();
      toast.success('Data perusahaan berhasil diperbarui');
    },
    onError: error => {
      errorHandler(error);
    },
  });
};

export const useSaveCompanyData = () => {
  const queryClient = useQueryClient();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  return useMutation({
    mutationFn: async ({ tenantId, payload }: { tenantId: string | number; payload: Partial<ICompanyData> }) => {
      const existingData = queryClient.getQueryData<ICompanyData | null>(['company-data', tenantId]);

      // pic_names already in correct format (array of strings), no transformation needed

      if (existingData?.id) {
        const url = `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/company-data/${existingData.id}`;
        // Explicitly remove auto-populated fields
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { start_date, end_date, pic_names, ...cleanPayload } = payload as any;
        const { data } = await axiosInstance.put<ICompanyData>(url, { ...cleanPayload, id: existingData.id });
        toast.success('Data perusahaan berhasil diperbarui');
        return data;
      }

      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/${tenantId}/company-data`;
      // Explicitly remove auto-populated fields
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { start_date, end_date, pic_names, ...cleanPayload } = payload as any;
      const { data } = await axiosInstance.post<ICompanyData>(url, cleanPayload);
      toast.success('Data perusahaan berhasil dibuat');
      return data;
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries(['company-data', variables.tenantId]);
      await refetchCompletionStatus();
    },
    onError: error => {
      errorHandler(error);
    },
  });
};
